/**
 * One-sentence, operator-language definitions for every recurring term a
 * first-day user meets (CLAR-08/CLAR-09). Single source of truth — render
 * through <HelpHint title={g.x.term}>{g.x.def}</HelpHint> or the Legend &
 * glossary card, never copy the wording inline so it can't drift between
 * pages.
 *
 * Language-aware since the Language Manager round: every entry carries
 * en + it ({term, def} each); de/es fall back to English (translate them
 * only when a full German/Spanish glossary is commissioned). Components
 * should use {@link useGlossary} (re-renders on language change); non-hook
 * call sites use {@link getGlossary}. The legacy {@link GLOSSARY} export
 * keeps the English shape for not-yet-swept callers.
 *
 * Definitions must match the REAL derivations in the data layer (e.g.
 * building load is the Grid device's total_kW — see types.ts) — a glossary
 * that contradicts the code is worse than none.
 */
import { useLanguage, type Lang } from "@/lib/i18n";

type Entry = { term: string; def: string };
type LocalizedEntry = { en: Entry; it: Entry };

const GLOSSARY_I18N = {
  soc: {
    en: {
      term: "SOC — State of charge",
      def: "How full the battery is, as a percentage of its usable capacity. 0% = empty (at the configured floor), 100% = full.",
    },
    it: {
      term: "SOC — Stato di carica",
      def: "Quanto è carica la batteria, in percentuale della capacità utile. 0% = scarica (al limite minimo configurato), 100% = piena.",
    },
  },
  dcBus: {
    en: {
      term: "DC bus voltage",
      def: "Voltage of the battery-side DC link. Units of the same model should sit close together — a unit drifting from its siblings is an early warning sign.",
    },
    it: {
      term: "Tensione del bus DC",
      def: "Tensione del collegamento DC lato batteria. Le unità dello stesso modello dovrebbero avere valori molto vicini — un'unità che si discosta dalle altre è un segnale d'allerta precoce.",
    },
  },
  reactive: {
    en: {
      term: "Reactive power (kVAr)",
      def: "Power that oscillates between grid and equipment without doing useful work. It loads cables and transformers, so grid operators care about it; it does not charge or discharge the battery.",
    },
    it: {
      term: "Potenza reattiva (kVAr)",
      def: "Potenza che oscilla tra rete e impianto senza compiere lavoro utile. Impegna cavi e trasformatori, perciò interessa al gestore di rete; non carica né scarica la batteria.",
    },
  },
  grid: {
    en: {
      term: "Grid",
      def: "Power measured at the grid coupling meter. + = importing from the grid, − = exporting to the grid.",
    },
    it: {
      term: "Rete",
      def: "Potenza misurata al contatore di scambio con la rete. + = prelievo dalla rete, − = immissione in rete.",
    },
  },
  netBuildingLoad: {
    en: {
      term: "Net building load",
      def: "The site's net consumption, from the grid meter's total. + = consuming, − = on-site generation exceeding load (PV surplus). Includes everything behind the meter except what the battery absorbs or adds.",
    },
    it: {
      term: "Carico netto del sito",
      def: "Il consumo netto del sito, dal totale del contatore di rete. + = consumo, − = la generazione in sito supera il carico (surplus fotovoltaico). Include tutto ciò che sta a valle del contatore, tranne ciò che la batteria assorbe o immette.",
    },
  },
  netAtMeter: {
    en: {
      term: "Net at meter",
      def: "What the utility actually sees at the coupling point after the battery and PV have done their work. + = net import.",
    },
    it: {
      term: "Netto al contatore",
      def: "Ciò che il distributore vede effettivamente al punto di scambio dopo l'intervento di batteria e fotovoltaico. + = prelievo netto.",
    },
  },
  selfSufficiency: {
    en: {
      term: "Self-sufficiency",
      def: "Share of the site's consumption covered by on-site sources (PV + battery) today instead of grid imports.",
    },
    it: {
      term: "Autosufficienza",
      def: "Quota del consumo del sito coperta oggi da fonti in sito (fotovoltaico + batteria) invece che dai prelievi dalla rete.",
    },
  },
  selfConsumption: {
    en: {
      term: "Self-consumption",
      def: "Share of on-site generation used by the site itself today instead of being exported.",
    },
    it: {
      term: "Autoconsumo",
      def: "Quota della generazione in sito utilizzata oggi dal sito stesso invece di essere immessa in rete.",
    },
  },
  dayAhead: {
    en: {
      term: "Day-ahead price",
      def: "The wholesale electricity price fixed one day in advance, one price per hour. Cheap hours are good times to charge from the grid.",
    },
    it: {
      term: "Prezzo day-ahead",
      def: "Il prezzo all'ingrosso dell'energia fissato il giorno prima, un prezzo per ogni ora. Le ore più economiche sono i momenti buoni per caricare dalla rete.",
    },
  },
  intraday: {
    en: {
      term: "Intraday price",
      def: "The latest traded price for an hour of the current day — updates as the day progresses and can deviate from the day-ahead price.",
    },
    it: {
      term: "Prezzo infragiornaliero",
      def: "L'ultimo prezzo scambiato per un'ora della giornata in corso — si aggiorna nel corso della giornata e può discostarsi dal prezzo day-ahead.",
    },
  },
  granularity: {
    en: {
      term: "Granularity",
      def: "The width of each averaged time bucket in a chart. Finer granularity shows more detail but covers a shorter window comfortably.",
    },
    it: {
      term: "Granularità",
      def: "L'ampiezza di ciascun intervallo mediato in un grafico. Una granularità più fine mostra più dettagli ma copre comodamente una finestra più breve.",
    },
  },
  cycle: {
    en: {
      term: "Battery cycle",
      def: "1 cycle = charging AND discharging the full nominal capacity once. 3,000 kWh through a 1,500 kWh fleet = 1 full cycle.",
    },
    it: {
      term: "Ciclo batteria",
      def: "1 ciclo = caricare E scaricare una volta l'intera capacità nominale. 3.000 kWh attraverso una flotta da 1.500 kWh = 1 ciclo completo.",
    },
  },
  watchdog: {
    en: {
      term: "Watchdog",
      def: "What the battery does if it stops hearing commands from the EMS: ramp to 0 kW, then switch off — a safety net against a dead control link.",
    },
    it: {
      term: "Watchdog",
      def: "Cosa fa la batteria se smette di ricevere comandi dall'EMS: rampa a 0 kW e poi si spegne — una rete di sicurezza contro la perdita del collegamento di controllo.",
    },
  },
  rampLimit: {
    en: {
      term: "Ramp limit",
      def: "The maximum speed at which commanded battery power may change, in kW per second. The inverter hardware imposes its own ramp on top.",
    },
    it: {
      term: "Limite di rampa",
      def: "La velocità massima con cui può variare la potenza comandata alla batteria, in kW al secondo. L'inverter impone in aggiunta la propria rampa hardware.",
    },
  },
  interrogation: {
    en: {
      term: "Interrogation",
      def: "The periodic full data refresh over the IEC-104 protocol link — all telemetry points are re-read, even ones that haven't changed.",
    },
    it: {
      term: "Interrogazione",
      def: "L'aggiornamento periodico completo dei dati sul collegamento IEC-104 — tutti i punti di telemetria vengono riletti, anche quelli che non sono cambiati.",
    },
  },
  severityOrder: {
    en: {
      term: "Alarm severity order",
      def: "Critical > Major > Minor > Warning (ThingsBoard convention — note that Warning ranks BELOW Minor here).",
    },
    it: {
      // Severity names stay English — they are ThingsBoard wire values.
      term: "Ordine di gravità degli allarmi",
      def: "Critical > Major > Minor > Warning (convenzione ThingsBoard — attenzione: qui Warning è MENO grave di Minor).",
    },
  },
  signConvention: {
    en: {
      term: "Sign convention",
      def: "Across this console: grid + = import, battery + = charging, − = discharging. Flow charts that plot the battery's contribution to the site bus invert this and say so in their legend.",
    },
    it: {
      term: "Convenzione dei segni",
      def: "In tutta questa console: rete + = prelievo, batteria + = in carica, − = in scarica. I grafici di flusso che mostrano il contributo della batteria al bus di sito invertono il segno e lo dichiarano nella legenda.",
    },
  },
} as const satisfies Record<string, LocalizedEntry>;

export type GlossaryKey = keyof typeof GLOSSARY_I18N;
export type Glossary = Record<GlossaryKey, Entry>;

const KEYS = Object.keys(GLOSSARY_I18N) as GlossaryKey[];

// One frozen record per language, built lazily — gives useGlossary()
// referential stability across renders (safe in deps arrays).
const cache = new Map<"en" | "it", Glossary>();

/** Localized glossary for non-hook callers. de/es fall back to English. */
export function getGlossary(lang: Lang): Glossary {
  const l: "en" | "it" = lang === "it" ? "it" : "en";
  let g = cache.get(l);
  if (!g) {
    const out = {} as Record<GlossaryKey, Entry>;
    for (const k of KEYS) out[k] = GLOSSARY_I18N[k][l];
    g = out;
    cache.set(l, g);
  }
  return g;
}

/**
 * React hook — the glossary in the active language, same shape as the
 * legacy GLOSSARY ({term, def} per key). Re-renders on language change.
 */
export function useGlossary(): Glossary {
  return getGlossary(useLanguage());
}

/**
 * Legacy English-only export for callers not yet swept to useGlossary().
 * Same keys + shape; does NOT react to language changes — sweep clusters
 * replace their uses with useGlossary().
 */
export const GLOSSARY: Glossary = getGlossary("en");
